"use client";

import type React from "react";

import { useEffect, useState } from "react";
import {
  Home,
  Building2,
  Calendar,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Star,
  MapPin,
  Coins,
} from "lucide-react";
import { Button } from "../UI/Button";
import { Avatar, AvatarFallback, AvatarImage } from "../UI/Avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../UI/dropdown-menu";
import { Link, useNavigate } from "react-router-dom";
import logo_dark from "../../assets/img/logo-negro.png";
import { useAdminContext } from "../../contexts/AdminProvider";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const { me }: any = useAdminContext();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const router = useNavigate();

  const handleLogout = () => {
    // Simular logout
    localStorage.removeItem("token");
    router("/");
  };

  useEffect(() => {
    if (sidebarOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    // Limpieza por si el componente se desmonta con el sidebar abierto
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [sidebarOpen]);

  const navigation = [
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: Home,
    },
    {
      name: "Alojamientos",
      href: "/alquileres",
      icon: Building2,
    },
    { name: "Excursiones", href: "/publicaciones", icon: MapPin },
    { name: "Reservas", href: "/reservas", icon: Calendar },
    {
      name: "Usuarios",
      href: "/usuarios",
      icon: Users,
      hidden: me?.role == "client",
    },
    { name: "Evaluaciones", href: "/info", icon: Star },
    {
      name: "Comisiones",
      href: "/comisiones",
      icon: Coins,
      hidden: me?.role == "client",
    },
    { name: "Configuracion", href: "/informacion", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50 font-poppins">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? "block" : "hidden"
          }`}
      >
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-full max-w-xs flex-col bg-white shadow-xl">
          <div className="flex h-16 items-center justify-between px-4 mt-5">
            <Link to={'/'}>
              <div className="flex items-center gap-2">
                <img src={logo_dark} alt="" />
              </div>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <nav className="flex-1 px-4 py-4">
            <ul className="space-y-2">
              {navigation.map((item) => (
                <li key={item.name}

                  className={`${item.hidden ? "hidden" : ""}`}>
                  <Link
                    to={item.href}
                    className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100 hover:text-gray-900"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <Link to={'/'}>
            <div className="flex items-center gap-2 px-4 py-3 border-b">
              <img src={logo_dark} alt="" className="object-cover" />
            </div>
          </Link>
          <nav className="flex-1 px-4 py-4">
            <ul className="space-y-2">
              {navigation.map((item) => (
                <li
                  key={item.name}
                  className={`${item.hidden ? "hidden" : ""}`}
                >
                  <Link
                    to={item.href}
                    className={`font-poppins flex items-center gap-3 px-3 py-2 text-sm font-medium text-gray rounded-md hover:bg-gray-100 hover:text-gray-900 `}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top navigation */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>
            </div>

            <div className="flex items-center gap-4">
              {/* Notifications */}
              <Button variant="ghost" size="icon" className="relative hidden">
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full text-xs"></span>
              </Button>

              <Link
                to={`/`}
                className="font-poppins cursor-pointer hover:opacity-50 px-4 py-2 border-0 border-white block rounded-md"
                style={{ fontSize: "12px" }}
              >
                Cambia al modo viajero
              </Link>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-8 w-8 rounded-full"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src="/placeholder.svg?height=32&width=32"
                        alt="Usuario"
                      />
                      <AvatarFallback>JD</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {`${me?.firstname} ${me?.lastname}`}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {`${me?.email}`}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <Users className="mr-2 h-4 w-4" />
                    <span>Perfil</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Cerrar sesión</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8 bg-gray-50">{children}</main>
      </div>
    </div>
  );
}
